/**
 * Created by enes.kahrovic@thebeaverhead.com on 24/10/2018.
 * Rebuilt per assets/handoffs/design_handoff_calendars_index.
 */

import React, { useCallback, useEffect, useRef, useState } from "react";

import { useHistory } from "react-router-dom";
import PageTemplate from "./../templates/PageTemplate";
import { getCalendarCurrentData } from "../../../actions/calendar";
import { useCalendarsStore } from "../../../hooks/redux/calendars";
import { useCalendarStore } from "../../../hooks/redux/calendar";
import { formatError } from "../../../helpers/errors";
import { copyTextToClipboard, updateJquery } from "../tools";
import { useCurrentOrganizationStore } from "../../../hooks/redux/currentOrganization";
import { getPublicWidgetUrl } from "../../../helpers/url";
import { WT } from "../consts/widget";
import { useSortableTable } from "../component/SortableTable/useSortableTable";
import { useSortableTablesStore } from "../../../hooks/redux/sortableTables";
import { useDebounce } from "../../../hooks/useDebounce";
import Button from "../theme/untitled/Button/Button";
import Card from "../theme/untitled/Card/Card";
import EmptyState from "../theme/untitled/EmptyState/EmptyState";
import Icon from "../theme/untitled/icons/Icon";
import Pagination from "../theme/untitled/Pagination/Pagination";
import { ToastProvider, useToast } from "../theme/untitled/Toast/ToastProvider";
import StatusBadge from "../component/WidgetIndex/StatusBadge";
import Footer from "../component/WidgetIndex/Footer";
import MajorToast from "../component/Toast/MajorToast";
import DeleteModal from "../component/WidgetIndex/DeleteModal";
import RowErrorStrip from "../component/WidgetIndex/RowErrorStrip";
import NoResultsState from "../component/WidgetIndex/NoResultsState";
import CalendarsTable from "./CalendarsPage/Table";
import RowMenu from "../component/WidgetIndex/RowMenu";
import Toolbar from "../component/WidgetIndex/Toolbar";
import { useCalendarsFilterSort } from "./CalendarsPage/useCalendarsFilterSort";
import { usePublishState } from "../component/WidgetIndex/usePublishState";
import {
  PAGE_SIZE,
  SORT_COLUMNS,
  getEditUrl,
  TEMPLATE_FILTER_OPTIONS,
} from "./CalendarsPage/constants";
import styles from "./CalendarsPage.module.scss";

// useToast() needs a ToastProvider ancestor, which can't be the same
// component instance that calls the hook — so the actual page content
// lives in this inner component, wrapped by the default export below.
const CalendarsPageContent = (props) => {
  const history = useHistory();
  const { calendars, fetchCalendars } = useCalendarsStore();
  const { calendar, updateCalendar, setCalendar, deleteCalendar } =
    useCalendarStore();
  const { currentOrganization } = useCurrentOrganizationStore();
  const showToast = useToast();
  const { sortableTables, setWidgetsSorting } = useSortableTablesStore();

  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search, 200);
  const [statusValues, setStatusValues] = useState([]);
  const [templateValues, setTemplateValues] = useState([]);

  const hasActiveFilters =
    !!debouncedSearch.trim() ||
    statusValues.length > 0 ||
    templateValues.length > 0;

  const filteredData = useCalendarsFilterSort(calendars.collection, {
    search: debouncedSearch,
    statusValues,
    templateValues,
  });

  const { sortedData } = useSortableTable(
    filteredData,
    SORT_COLUMNS,
    sortableTables.columns[WT.calendar].field,
    sortableTables.columns[WT.calendar].order
  );

  const [page, setPage] = useState(1);
  const pageCount = Math.max(1, Math.ceil(sortedData.length / PAGE_SIZE));
  const pagedData = sortedData.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const tableTopRef = useRef(null);
  const [liveAnnouncement, setLiveAnnouncement] = useState("");

  // Page resets whenever the set being paginated changes shape, so a user
  // can't land on an out-of-range page after narrowing the list further.
  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, statusValues, templateValues]);

  const onPageChange = useCallback(
    (nextPage) => {
      setPage(nextPage);
      tableTopRef.current?.focus();
      const start = (nextPage - 1) * PAGE_SIZE + 1;
      const end = Math.min(nextPage * PAGE_SIZE, sortedData.length);
      setLiveAnnouncement(
        `Showing ${start} to ${end} of ${sortedData.length} calendars`
      );
    },
    [sortedData.length]
  );

  const clearFilters = useCallback(() => {
    setSearch("");
    setStatusValues([]);
    setTemplateValues([]);
  }, []);

  useEffect(() => {
    fetchCalendars();
  }, []);

  useEffect(() => {
    /**
     * If no calendars available, navigate to new calendar page.
     * Handle back button so user does not enter an infinite loop.
     */
    if (calendars.fetchSuccess && !calendars.collection.length) {
      if (history.action === "POP" && props) {
        history.go(-1);
      } else {
        history.push("/calendar/new");
      }
    }
    updateJquery();
  }, [calendars.fetchSuccess]);

  const { pendingUuid, failedRow, togglePublish, dismissError } =
    usePublishState({
      item: calendar,
      updateItem: updateCalendar,
      setItem: setCalendar,
      getCurrentData: getCalendarCurrentData,
    });

  // Duplicate shares the same `calendar.updateError`/`updateSuccess` signal
  // as publish (both dispatch through updateCalendar), but isn't covered by
  // the row error strip above (that's publish-only) — this is its own
  // pending-tracker so a Duplicate outcome still surfaces, without
  // double-firing alongside a publish outcome that's already being shown
  // inline. See usePublishState.js for why a watch-effect (not try/catch)
  // is required here: updateCalendar's own promise never rejects. Holds the
  // source calendar's name (not just a boolean) so the toast can name it.
  const cloningRef = useRef(null);

  const onClone = useCallback(
    (calendar) => {
      const calendarData = getCalendarCurrentData(calendar);
      calendarData.uuid = null;
      calendarData.available = 0;
      calendarData.name = "Copy of " + calendarData.name;

      cloningRef.current = { name: calendar.name };
      updateCalendar(null, calendarData);
    },
    [updateCalendar]
  );

  useEffect(() => {
    if (!cloningRef.current) {
      return;
    }

    if (calendar.updateError) {
      showToast({
        variant: "error",
        message: `Could not duplicate "${
          cloningRef.current.name
        }". ${formatError(calendar.updateError).join(" ")}`,
      });
      cloningRef.current = null;
    } else if (calendar.updateSuccess) {
      showToast({
        variant: "success",
        message: `"${cloningRef.current.name}" duplicated.`,
      });
      cloningRef.current = null;
      setCalendar(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [calendar.updateError, calendar.updateSuccess]);

  // Delete: only one calendar can be mid-delete at a time, and the
  // confirm/deleting/failed states map directly onto the `calendar`
  // (singular) reducer's own delete/deleteError/deleteSuccess flags —
  // already correctly wired, no reducer changes needed. See §7.5-7.8.
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deletedToastName, setDeletedToastName] = useState(null);
  const deleteToastTimeoutRef = useRef(null);

  const onConfirmDelete = useCallback(
    (target) => deleteCalendar(target.uuid),
    [deleteCalendar]
  );

  const dismissDeleteToast = useCallback(() => {
    clearTimeout(deleteToastTimeoutRef.current);
    setDeletedToastName(null);
  }, []);

  useEffect(() => {
    if (!deleteTarget) {
      return;
    }
    if (calendar.deleteSuccess) {
      setDeletedToastName(deleteTarget.name);
      clearTimeout(deleteToastTimeoutRef.current);
      deleteToastTimeoutRef.current = setTimeout(
        () => setDeletedToastName(null),
        6000
      );
      setDeleteTarget(null);
      setCalendar(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [calendar.deleteSuccess]);

  useEffect(() => () => clearTimeout(deleteToastTimeoutRef.current), []);

  const onCreateCalendar = useCallback(
    () => history.push("/calendar/new"),
    [history]
  );

  const onCopyLink = useCallback(
    (calendar) => {
      copyTextToClipboard(
        getPublicWidgetUrl(
          `c/${calendar.slug}`,
          currentOrganization.model.widget_domain
        )
      );
      showToast({ variant: "success", message: "Link copied" });
    },
    [currentOrganization.model, showToast]
  );

  const isMutating = calendars.fetch;
  const isInitialLoading = calendars.fetch && !calendars.collection.length;

  const sortBy = sortableTables.columns[WT.calendar].field
    ? {
        key: sortableTables.columns[WT.calendar].field,
        direction: sortableTables.columns[WT.calendar].order || "asc",
      }
    : undefined;

  return (
    <PageTemplate
      sidebarId="calendar"
      title="Calendars"
      subtitle={`${currentOrganization.model.name} calendar widgets.`}
      icon={"event"}
      headerControls={[
        <Button
          key="create-calendar"
          iconLeading={<Icon name="Plus" />}
          onClick={onCreateCalendar}
        >
          Create calendar
        </Button>,
      ]}
    >
      <Card className={styles.card}>
        <Toolbar
          search={search}
          onSearchChange={setSearch}
          statusValues={statusValues}
          onStatusChange={setStatusValues}
          itemLabelPlural="calendars"
          templateFilterOptions={TEMPLATE_FILTER_OPTIONS}
          templateValues={templateValues}
          onTemplateChange={setTemplateValues}
        />
        <Card.Body noPadding>
          <div
            className={styles.tableScroll}
            ref={tableTopRef}
            tabIndex={-1}
            aria-label="Calendars table"
          >
            {isInitialLoading ||
            (!calendars.collection.length && !hasActiveFilters) ? (
              <EmptyState
                icon={<Icon name="Calendar2" size={24} />}
                title="No calendars yet"
                body="Create your first calendar widget to get started."
              />
            ) : sortedData.length === 0 ? (
              <NoResultsState
                onClear={clearFilters}
                itemLabelPlural="calendars"
              />
            ) : (
              <CalendarsTable
                rows={pagedData}
                sortBy={sortBy}
                onSortChange={({ key, direction }) =>
                  setWidgetsSorting(WT.calendar, key, direction)
                }
                isRowPublishing={(row) => pendingUuid === row.uuid}
                renderStatus={(row) => (
                  <StatusBadge
                    item={row}
                    isPublishing={pendingUuid === row.uuid}
                  />
                )}
                renderMenu={(row) => (
                  <RowMenu
                    item={row}
                    itemLabel="calendar"
                    editUrl={getEditUrl(row)}
                    onDuplicate={onClone}
                    onCopyLink={onCopyLink}
                    onTogglePublish={togglePublish}
                    onDelete={setDeleteTarget}
                    disabled={isMutating}
                  />
                )}
                rowExtra={(row) =>
                  failedRow?.uuid === row.uuid ? (
                    <RowErrorStrip
                      item={row}
                      message={failedRow.message}
                      onRetry={() => togglePublish(row)}
                      onDismiss={dismissError}
                      colSpan={5}
                    />
                  ) : null
                }
              />
            )}
          </div>
        </Card.Body>
        <div aria-live="polite" className="sr-only">
          {liveAnnouncement}
        </div>
        <Footer
          currentOrganization={currentOrganization}
          itemLabel="calendar"
          publishedCount={
            calendars.collection.filter((c) => c.available === 1).length
          }
          rangeStart={pagedData.length ? (page - 1) * PAGE_SIZE + 1 : 0}
          rangeEnd={(page - 1) * PAGE_SIZE + pagedData.length}
          total={sortedData.length}
          totalCount={calendars.collection.length}
          pagination={
            sortedData.length > PAGE_SIZE ? (
              <Pagination
                variant="numbered"
                page={page}
                totalPages={pageCount}
                onPageChange={onPageChange}
              />
            ) : null
          }
        />
      </Card>
      <DeleteModal
        item={deleteTarget}
        itemLabel="calendar"
        deleting={calendar.delete}
        deleteErrorMessage={
          calendar.deleteError
            ? formatError(calendar.deleteError).join(" ")
            : null
        }
        onConfirm={onConfirmDelete}
        onCancel={() => setDeleteTarget(null)}
      />
      {deletedToastName && (
        <MajorToast
          title={deletedToastName}
          text="Has stopped been permamently deleted."
          onDismiss={dismissDeleteToast}
        />
      )}
    </PageTemplate>
  );
};

const CalendarsPage = (props) => (
  <ToastProvider>
    <CalendarsPageContent {...props} />
  </ToastProvider>
);

export default CalendarsPage;
